import React from 'react'
import debounce from 'lodash.debounce'

const eventTypes = ['mousedown', 'touchstart'] as ['mousedown', 'touchstart']

function hasClassInHierarchy(element: Element, className: string): boolean {
    if (element.classList.contains(className)) {
        return true
    }
    return element.parentElement && hasClassInHierarchy(element.parentElement, className)
        ? true
        : false
}

export type ClickOutsideEvent = MouseEvent | TouchEvent
export type ClickOutsideEventTrigger = (e: ClickOutsideEvent) => void

export interface ClickOutsideOptions {
    filterClasses?: string[]
    enabled?: boolean
}

export interface OnClickOutsideProps {
    setClickOutsiteTrigger: (newTrigger: ClickOutsideEventTrigger) => void
    clickOutsideRef: React.RefObject<any>
}

export interface WrapperProps {
    innerRef?: ((instance: any) => void) | React.RefObject<any> | null | undefined
    onClickOutsideOptions?: ClickOutsideOptions
}

type WrappedComponentBase<Props> =
    | React.FunctionComponent<OnClickOutsideProps & WrapperProps & Props>
    | React.ComponentClass<OnClickOutsideProps & WrapperProps & Props>

/**
 * A React hook for triggering a fuction when a user clicks outside of a particular element
 * @param {React.RefObject<HTMLElement | null>} ref - A react ref object, this should be the element you want to track outside clicks
 * @param {(e: MouseEvent | TouchEvent) => void} trigger - A function that is triggered when a click occurs outside
 * @param {Options.filterClasses} filterClasses - An optional array of classNames which will not trigger the outside click
 * @param {Options.enabled} enabled - Set "enabled" to false to not add the click effect to the page
 */
export const useOnClickOutside = (
    ref: React.RefObject<HTMLElement>,
    trigger: ClickOutsideEventTrigger,
    options?: ClickOutsideOptions
) => {
    React.useEffect(() => {
        if (options && options.enabled === false) {
            return
        }

        // Handler is debounced, that way if both a mouse event and a touch event are called, they will
        // only trigger once
        const handleClickOutside = debounce(
            (e: MouseEvent | TouchEvent) => {
                const isInside =
                    e.target instanceof Element && ref.current && ref.current.contains(e.target)

                if (isInside) return

                const inClassfilter =
                    options &&
                    options.filterClasses &&
                    options.filterClasses.some((className) => {
                        if (e.target instanceof Element) {
                            return hasClassInHierarchy(e.target, className)
                        }
                        return false
                    })

                if (inClassfilter) return
                trigger(e)
            },
            0,
            { leading: true, trailing: false }
        )

        for (const eventName of eventTypes) {
            document.addEventListener(eventName, handleClickOutside, false)
        }

        return function cleanupOnClickOutside() {
            for (const eventName of eventTypes) {
                document.removeEventListener(eventName, handleClickOutside, false)
            }
        }
    })
}

/**
 * A higher order component that implements the useOnClickOutside interface. Good for non-functional
 * components. This will setup the ref you need to track, and pass it down to the props. Your component
 * should accept `OnClickOutsideProps` in its props.
 */
export const wrapOnClickOutside = <Props extends {}>(component: WrappedComponentBase<Props>) => {
    const WrappedComponent: React.FC<WrapperProps & Props> = (props) => {
        const ref = React.useRef<HTMLElement>(null)
        const trigger = React.useRef<ClickOutsideEventTrigger | null>(null)

        const setClickOutsiteTrigger = (newTrigger: ClickOutsideEventTrigger) =>
            (trigger.current = newTrigger)

        useOnClickOutside(
            ref,
            (e) => {
                if (trigger.current) {
                    trigger.current(e)
                }
            },
            props.onClickOutsideOptions
        )

        return React.createElement(component, {
            setClickOutsiteTrigger: setClickOutsiteTrigger,
            clickOutsideRef: ref,
            ...props,
        })
    }

    return WrappedComponent
}
